import { SessionStorageKeys } from 'common/storage/constants';
import { QueryParams } from 'common/urls/QueryParams';
import { useEffect, useState } from 'react';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useNonstrictSessionStorage } from 'src/shared/storage/Storage';
import { isDevEnvironment, isPreviewEnvironment } from 'src/shared/utils/EnvironmentUtilities';
import { match } from 'ts-pattern';
import { StringParam, useQueryParam } from 'use-query-params';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export enum MotionOverride {
  ON = 'on',
  OFF = 'off',
}

/**
 * Whether to show this viewer a still page, as `prefers-reduced-motion` asks. `?motion=on|off`
 * overrides it for review, on dev and preview builds and for admins only. Forcing motion back on
 * would otherwise override a real accessibility preference for anyone handed the link, and it is
 * kept for the tab, so there would be no way out short of closing it.
 *
 * A URL parameter beats the kept value, so a second `?motion=` takes effect at once.
 */
export const useReducedMotion = (): boolean => {
  const [currentUser] = useCurrentUser();
  const canOverride = Boolean(isDevEnvironment() || isPreviewEnvironment() || currentUser?.admin);
  const [rawMotionParam] = useQueryParam(QueryParams.MOTION, StringParam);
  const [storedOverride, setStoredOverride] = useNonstrictSessionStorage<string>(
    SessionStorageKeys.MOTION_OVERRIDE,
    undefined
  );
  const motionParam = canOverride ? rawMotionParam : undefined;
  const storedIfAllowed = canOverride ? storedOverride : undefined;
  const [systemPrefersReduced, setSystemPrefersReduced] = useState(
    () => window.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia?.(REDUCED_MOTION_QUERY);
    if (!mediaQuery) {
      return undefined;
    }

    const syncFromSystem = () => setSystemPrefersReduced(mediaQuery.matches);
    mediaQuery.addEventListener('change', syncFromSystem);
    return () => mediaQuery.removeEventListener('change', syncFromSystem);
  }, []);

  useEffect(() => {
    if (motionParam === MotionOverride.ON || motionParam === MotionOverride.OFF) {
      setStoredOverride(motionParam);
    }
  }, [motionParam, setStoredOverride]);

  return match(motionParam ?? storedIfAllowed)
    .with(MotionOverride.ON, () => false)
    .with(MotionOverride.OFF, () => true)
    .otherwise(() => systemPrefersReduced);
};
